import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import {
  color,
  device,
  radius,
  SmallText,
  shadow,
  space,
  fontWeight,
  Button,
  Pill,
  H3,
} from '@ticketswap/solar'
import ListCartDialog from './ListCardDialog'
import ListItem from './ListItem'
import { css } from '@emotion/react'
import ListImageRow from '~/components/ListCard/ListImageRow'
import { eventListFragmentPropTypes } from '~/graphql/fragments/eventList'

const ListCardContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${radius.lg};
  min-height: ${space[256]};
  box-shadow: ${shadow.strong};
  display: block;
  background-color: ${color.space};

  [data-theme='dark'] & {
    border: 1px solid ${color.spaceLightest};
    background-color: ${color.nova};
  }

  ${({ hasThumbnailImage }) =>
    !hasThumbnailImage &&
    css`
      min-height: auto;
    `};
`

const BannerContent = styled.div`
  z-index: 1;
  color: ${color.nova};
  justify-content: flex-end;
  min-height: ${192 / 16}rem;
  display: flex;
  flex-direction: column;

  h3 {
    font-weight: ${fontWeight.bold};
    color: ${color.nova};

    [data-theme="dark"] & {
      color: ${color.space};
    }
  }

  span {
    display: block;
    color: ${color.nova};

    [data-theme="dark"] & {
      color: ${color.space};
    }
  }

  @media ${device.tablet} {
    min-height: ${space[256]};
  }

  ${({ hasThumbnailImage }) =>
    !hasThumbnailImage &&
    css`
      min-height: auto;
      color: ${color.space};
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid ${color.spaceLightest};
      background: ${color.nova};

      h3 {
        color: ${color.space};
      }

      span {
        color: ${color.space};
      }

      [data-theme='dark'] & {
        color: ${color.nova};
      }

      @media ${device.tablet} {
        min-height: auto;
      }
    `}}
`

const TextWrapper = styled.div`
  padding: ${space[64]} ${space[16]} ${space[16]};
  background-image: linear-gradient(
    180deg,
    rgba(0, 19, 25, 0) 0%,
    rgba(0, 19, 25, 0.07) 28%,
    rgba(0, 19, 25, 0.4) 58.21%,
    rgba(0, 19, 25, 0.9) 100%
  );
  z-index: 1;

  @media ${device.tablet} {
    padding: ${space[64]} ${space[24]} ${space[24]};
  }

  ${({ hasThumbnailImage }) =>
    !hasThumbnailImage &&
    css`
      background-image: none;
      padding: ${space[24]} ${space[16]} ${space[16]};

      @media ${device.tablet} {
        padding: ${space[16]};
      }
    `};
`

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.image});
  width: 100%;
  height: ${space[256]};

  ${({ logoUrl }) =>
    logoUrl &&
    css`
      ::after {
        content: '';
        position: absolute;
        top: ${space[56]};
        bottom: ${space[56]};
        right: 0;
        left: 0;
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        background-image: url(${logoUrl});
      }
    `}
`

const Content = styled.div`
  position: relative;
  grid-column: 2;
  width: 100%;
  background-color: ${color.nova};
`

const ListButtonItem = styled.li`
  padding: ${space[16]};
  border-top: 1px solid ${color.spaceLightest};
`

const StyledPill = styled(Pill)`
  position: absolute;
  top: ${space[8]};
  right: ${space[8]};
  z-index: 1;

  @media ${device.tablet} {
    right: auto;
    left: ${space[8]};

    ${({ hasThumbnailImage }) =>
      !hasThumbnailImage &&
      css`
        top: auto;
        left: auto;
        bottom: ${space[4]};
        right: ${space[4]};
      `}
  }
`

export const ListCard = ({
  t,
  title,
  text,
  dialogItems,
  hasNextPage,
  onShowMore,
  numberOfItemsOnCard,
  imageThumbnailUrl,
  imageBackgroundUrl,
  logoUrl,
  thumbnailImages,
  variant,
  employeeOnly,
  trackClick,
  trackSeeAll,
}) => {
  const [showDialog, setShowDialog] = useState(false)
  const activeDialogItems = dialogItems.filter(({ node }) => node?.__typename === 'ActiveEvent')

  if (activeDialogItems.length === 0) return null

  // Image to show on card
  const thumbnailImage = imageThumbnailUrl
  // Image to show on the dialog
  const backgroundImage = imageBackgroundUrl || activeDialogItems[0].node?.imageUrl
  const cardItems = activeDialogItems.slice(0, numberOfItemsOnCard)
  const hasThumbnailImage = variant !== 'NO_THUMBNAIL'
  const showSeeAll = activeDialogItems.length > numberOfItemsOnCard

  const handleSeeAll = () => {
    // If there are more items, fetch them to display in the dialog
    if (hasNextPage) {
      onShowMore()
    }

    setShowDialog(true)
    trackSeeAll && trackSeeAll()
  }

  return (
    <ListCardContainer hasThumbnailImage={hasThumbnailImage}>
      {employeeOnly && (
        <StyledPill variant="warning" hasThumbnailImage={hasThumbnailImage}>
          Employee only
        </StyledPill>
      )}
      <BannerContent hasThumbnailImage={hasThumbnailImage}>
        <TextWrapper hasThumbnailImage={hasThumbnailImage}>
          <H3>{title}</H3>
          {text && <SmallText>{text}</SmallText>}
        </TextWrapper>
      </BannerContent>
      {variant === 'THUMBNAIL' && <BackgroundImage image={thumbnailImage} logoUrl={logoUrl} />}
      {['THUMBNAIL_TRACK', 'AVATARS'].includes(variant) && (
        <ListImageRow thumbnailImages={thumbnailImages} hasAvatars={variant === 'AVATARS'} />
      )}

      <Content>
        <ul>
          {cardItems.map(({ node }) => {
            return <ListItem key={node.id} t={t} event={node} trackClick={trackClick} />
          })}

          {showSeeAll && (
            <ListButtonItem>
              <Button variant="secondary" size="small" width="full" onClick={handleSeeAll}>
                {t('common:generic.seeAll')}
              </Button>
            </ListButtonItem>
          )}
        </ul>
      </Content>

      <ListCartDialog
        t={t}
        showDialog={showDialog}
        items={activeDialogItems}
        hasNextPage={hasNextPage}
        onShowMore={onShowMore}
        title={title}
        text={text}
        backgroundImage={backgroundImage || thumbnailImage}
        logoUrl={logoUrl}
        onDismiss={() => setShowDialog(false)}
      />
    </ListCardContainer>
  )
}

ListCard.defaultProps = {
  hasNextPage: false,
  numberOfItemsOnCard: 3,
  employeeOnly: false,
  thumbnailImages: [],
}

ListCard.propTypes = {
  t: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  variant: PropTypes.oneOf(['NO_THUMBNAIL', 'THUMBNAIL', 'THUMBNAIL_TRACK', 'AVATARS']).isRequired,
  dialogItems: PropTypes.arrayOf(
    PropTypes.shape({
      node: eventListFragmentPropTypes,
    })
  ).isRequired,
  hasNextPage: PropTypes.bool,
  onShowMore: PropTypes.func,
  numberOfItemsOnCard: PropTypes.number,
  imageThumbnailUrl: PropTypes.string,
  imageBackgroundUrl: PropTypes.string,
  logoUrl: PropTypes.string,
  thumbnailImages: PropTypes.arrayOf(PropTypes.string),
  employeeOnly: PropTypes.bool,
  trackClick: PropTypes.func.isRequired,
  trackSeeAll: PropTypes.func,
}
